// frontend/src/pages/admin/ShowcaseModeration.tsx
import React, { useEffect, useState } from 'react';
import { adminShowcaseAPI } from '@services/showcaseApi';
import type { AdminSubmission } from '@/types/showcase';

const ShowcaseModeration: React.FC = () => {
  const [items, setItems] = useState<AdminSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminShowcaseAPI.listPending();
      setItems(res.data ?? []);
    } catch {
      setError('Could not load the moderation queue.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const removeItem = (eventId: string) =>
    setItems(prev => prev.filter(i => i.eventId !== eventId));

  const onApprove = async (eventId: string) => {
    setBusyId(eventId);
    setError(null);
    try {
      await adminShowcaseAPI.approve(eventId);
      removeItem(eventId);
    } catch {
      setError('Could not approve the submission.');
    } finally {
      setBusyId(null);
    }
  };

  const onConfirmReject = async (eventId: string) => {
    if (!reason.trim()) return;
    setBusyId(eventId);
    setError(null);
    try {
      await adminShowcaseAPI.reject(eventId, reason.trim());
      removeItem(eventId);
      setRejectingId(null);
      setReason('');
    } catch {
      setError('Could not reject the submission.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Showcase Moderation</h1>
      <p className="text-gray-600 mb-6">Approve or reject albums submitted to the public showcase.</p>

      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

      {loading ? (
        <p className="text-gray-400">Loading queue…</p>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 p-12 text-center">
          <h3 className="text-lg font-medium text-gray-900">No pending submissions</h3>
          <p className="text-sm text-gray-500">New submissions appear here for review.</p>
        </div>
      ) : (
        <ul className="space-y-4">
          {items.map(sub => (
            <li key={sub.eventId} className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-4">
                <div className="h-20 w-32 flex-shrink-0 overflow-hidden rounded bg-gray-100">
                  <img
                    src={sub.coverImage || '/placeholder-event.jpg'}
                    alt={sub.eventTitle}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">{sub.eventTitle}</h3>
                  <p className="text-sm text-gray-600">{sub.photographerBusinessName}</p>
                  <a
                    href={sub.micrositeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-indigo-600 hover:underline"
                  >
                    {sub.micrositeUrl}
                  </a>
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onApprove(sub.eventId)}
                    disabled={busyId === sub.eventId}
                    className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setRejectingId(sub.eventId);
                      setReason('');
                    }}
                    disabled={busyId === sub.eventId}
                    className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                  >
                    Reject
                  </button>
                </div>
              </div>

              {rejectingId === sub.eventId && (
                <div className="mt-4 rounded-md bg-red-50 border border-red-200 p-4">
                  <label
                    htmlFor={`reject-reason-${sub.eventId}`}
                    className="block text-sm font-medium text-gray-700"
                  >
                    Rejection reason
                  </label>
                  <textarea
                    id={`reject-reason-${sub.eventId}`}
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    rows={2}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    placeholder="Why is this album being rejected?"
                  />
                  <div className="mt-3 flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setRejectingId(null);
                        setReason('');
                      }}
                      className="rounded-md px-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => onConfirmReject(sub.eventId)}
                      disabled={!reason.trim() || busyId === sub.eventId}
                      className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      Confirm reject
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ShowcaseModeration;
